import type {
  StaffCustomerList,
  StaffCustomerMemoryView,
  StaffMemoryFactDetail,
} from "@lafayette/schemas";
import { useQuery } from "@tanstack/react-query";

import { useMemoryRuntime } from "./runtime";

// The reads of the customer memory pages. A staff answer holds what a customer said, so nothing stays
// in the cache after the page lets go (`gcTime: 0`), and nothing is written to browser storage. A failed
// read is not retried by the library: the page shows the error and offers a retry.

export const memoryKeys = {
  all: ["customer-memory"] as const,
  customers: (limit: number) => ["customer-memory", "customers", limit] as const,
  workspace: (customerId: string, includeClosed: boolean) =>
    ["customer-memory", "workspace", customerId, includeClosed] as const,
  fact: (factId: string) => ["customer-memory", "fact", factId] as const,
};

const READ_OPTIONS = {
  gcTime: 0,
  retry: false,
  refetchOnWindowFocus: false,
} as const;

export const CUSTOMER_LIST_PAGE = 50;
export const CUSTOMER_LIST_MAX = 100;

/** M-S1. The customers the staff member may open. `limit` grows to the contract maximum on request. */
export function useCustomerList(limit: number, enabled: boolean) {
  const { api } = useMemoryRuntime();
  return useQuery<StaffCustomerList>({
    queryKey: memoryKeys.customers(limit),
    queryFn: ({ signal }) => api.listCustomers({ limit }, { signal }),
    enabled,
    ...READ_OPTIONS,
  });
}

/** M-S2. The workspace of one customer. `includeClosed` adds the history. */
export function useCustomerMemory(customerId: string, includeClosed: boolean, enabled: boolean) {
  const { api } = useMemoryRuntime();
  return useQuery<StaffCustomerMemoryView>({
    queryKey: memoryKeys.workspace(customerId, includeClosed),
    queryFn: ({ signal }) => api.getCustomerMemory(customerId, { includeClosed }, { signal }),
    enabled,
    ...READ_OPTIONS,
  });
}

/** M-S3. One fact with its evidence, run, supersession chain and audit trail. */
export function useMemoryFact(factId: string, enabled: boolean) {
  const { api } = useMemoryRuntime();
  return useQuery<StaffMemoryFactDetail>({
    queryKey: memoryKeys.fact(factId),
    queryFn: ({ signal }) => api.getFact(factId, { signal }),
    enabled,
    ...READ_OPTIONS,
  });
}
